import type { UseDashboardType } from '@/state/useDashboard';
import type { ExperimentCounts } from '@/state/experiments/useExperimentsList';
import type { StatusInput } from './dashboard-metrics';
import { MetricGrid, MetricTile, Panel } from '../ConsolePanel';
import { buildStatusMetrics } from './dashboard-metrics';

type MetricsProps = {
  dashboardManager: UseDashboardType;
  /** The experiment counts from the list, for a website whose stats have not been built yet. */
  counts: ExperimentCounts | null;
};

/**
 * A website's stats row is only written once its first sessions have been processed, so
 * a new website has none. The counts are known from the experiments list regardless, and
 * a Status panel reading "0 running, 1 draft" says far more than one that is missing.
 */
const statusFromCounts = (counts: ExperimentCounts): StatusInput => ({
  experimentSessions: 0,
  recentSessions: 0,
  runningCount: counts.Running,
  // Drafts in QA are drafts here, as in the stats cron's draftCount.
  draftCount: counts.Draft + counts.QA,
  deployedCount: counts.Deployed,
});

/**
 * Tone by meaning, keyed on the tile's own title.
 *
 * The titles come from `dashboard-metrics` and are rendered verbatim, so they
 * are also the only stable handle a consumer has for "which tile is this".
 * Anything unlisted stays neutral rather than borrowing a colour it has not
 * earned.
 */
const STATUS_TONE: Record<string, string> = {
  'Running Experiments': 'text-ok',
  Drafts: 'text-sel',
  Deployed: 'text-accent',
};

export function Metrics({ dashboardManager, counts }: MetricsProps) {
  const { dashboardStats, isLoading } = dashboardManager;

  const status = dashboardStats ?? (!isLoading && counts ? statusFromCounts(counts) : null);
  if (!status) return null;

  const metrics = buildStatusMetrics(status);

  return (
    <Panel title="Status" description="Where the experimentation programme stands right now." tourAnchor="home-status">
      <MetricGrid>
        {metrics.map((metric) => (
          <MetricTile
            key={metric.title}
            label={metric.title}
            value={metric.value}
            valueClassName={STATUS_TONE[metric.title]}
            description={metric.description}
            subtitle={metric.subtitle}
          />
        ))}
      </MetricGrid>
    </Panel>
  );
}
